import React from "react";
import { useNavigate } from "react-router-dom";
import { Check, Info } from "lucide-react";
import { HelpTooltip } from "@/components/HelpTooltip";
import { ExchangeLogo } from "@/components/ui/ExchangeLogo";
import {
  useAvailableExchanges,
  useUpbitAnnouncementKey,
  useUserExchanges,
} from "@/hooks/useExchanges";
import { UPBIT_CODE } from "@/utils/exchange-config";

interface ExchangesStripCardProps {
  userId: string;
  className?: string;
  /** 40 on desktop, smaller on phones so seven logos fit. */
  logoSize?: number;
}

/**
 * "Exchanges · n / 7 Connected" card with a row of exchange logos (Trade
 * page). Connected ones get a green ring; any logo opens Settings.
 */
export const ExchangesStripCard: React.FC<ExchangesStripCardProps> = ({
  userId,
  className = "",
  logoSize = 40,
}) => {
  const navigate = useNavigate();
  const { data: available = [] } = useAvailableExchanges();
  const { data: userExchanges = [] } = useUserExchanges(userId);
  const { data: upbitKey } = useUpbitAnnouncementKey(userId);

  const connected = new Set(userExchanges.map((exchange) => exchange.code));
  if (upbitKey?.connected) connected.add(UPBIT_CODE);
  const exchanges = [
    ...available.map((exchange) => ({ code: exchange.exchangeCode, name: exchange.exchangeName })),
    { code: UPBIT_CODE, name: "Upbit" },
  ];

  return (
    <div className={`glass flex flex-col gap-4 p-4 ${className}`}>
      <div className="flex items-center gap-1">
        <span className="text-sm font-semibold leading-5 text-white">Exchanges</span>
        <HelpTooltip text="Connect an exchange with its API keys to trade and autotrade from Trigger. Upbit keys give faster alerts.">
          <Info className="size-4 text-[#a3a3a3]" aria-label="About exchanges" />
        </HelpTooltip>
        <span className="flex-1 text-right text-sm font-semibold leading-5 text-[#00c27e]">
          {connected.size} / {exchanges.length} Connected
        </span>
      </div>
      <div className="flex items-center justify-between gap-2">
        {exchanges.map((exchange) => {
          const isConnected = connected.has(exchange.code);
          return (
            <button
              key={exchange.code}
              type="button"
              onClick={() => navigate("/settings")}
              aria-label={`Exchange settings: ${exchange.name}, ${isConnected ? "connected" : "not connected"}`}
              className={`relative shrink-0 rounded-full transition-transform hover:scale-105 ${
                isConnected ? "ring-2 ring-[#2dcc89] ring-offset-2 ring-offset-[#0d121a]" : ""
              }`}
            >
              <ExchangeLogo code={exchange.code} size={logoSize} />
              {isConnected && (
                <span className="absolute -bottom-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-[#07a66c]">
                  <Check className="size-3 text-white" strokeWidth={3} />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
